import React, { useState, useEffect } from 'react';
import { Plus, Search, BookOpen } from 'lucide-react';
import type { Serie } from '../services/seriesService';
import { seriesService } from '../services/seriesService';
import type { CreateSerieDto, UpdateSerieDto } from '../services/seriesService';
import { SerieCard } from '../components/SerieCard';
import { SerieFormModal } from '../components/SerieFormModal';
import { ConfirmDeleteSerieModal } from '../components/ConfirmDeleteSerieModal';
import Layout from '../components/Layout';
import toast from 'react-hot-toast';
import styles from './SeriesPage.module.css';

const SeriesPage: React.FC = () => {
  const [series, setSeries] = useState<Serie[]>([]);
  const [filteredSeries, setFilteredSeries] = useState<Serie[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [tipoFilter, setTipoFilter] = useState<'all' | 'curricular' | 'extracurricular'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'ativa' | 'inativa'>('all');

  // Modais
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingSerie, setEditingSerie] = useState<Serie | null>(null);
  const [deletingSerie, setDeletingSerie] = useState<Serie | null>(null);

  // Carregar séries
  const loadSeries = async () => {
    try {
      setLoading(true);
      const data = await seriesService.getAll();
      setSeries(data);
    } catch (error) {
      console.error('Erro ao carregar séries:', error);
      toast.error('Erro ao carregar séries');
    } finally {
      setLoading(false);
    }
  };

  // Efeito inicial
  useEffect(() => {
    loadSeries();
  }, []);

  // Filtrar séries
  useEffect(() => {
    let filtered = series;

    // Filtro por busca
    if (searchTerm) {
      filtered = filtered.filter(serie =>
        serie.nome.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtro por tipo
    if (tipoFilter !== 'all') {
      filtered = filtered.filter(serie => serie.tipoEnsino === tipoFilter);
    }

    // Filtro por status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(serie => {
        return statusFilter === 'ativa' ? serie.ativa : !serie.ativa;
      });
    }

    setFilteredSeries(filtered);
  }, [series, searchTerm, tipoFilter, statusFilter]);

  // Estatísticas
  const stats = {
    total: series.length,
    ativas: series.filter(s => s.ativa).length,
    inativas: series.filter(s => !s.ativa).length,
    curriculares: series.filter(s => s.tipoEnsino === 'curricular').length,
    extracurriculares: series.filter(s => s.tipoEnsino === 'extracurricular').length,
  };

  // Handlers
  const handleCreateSerie = async (data: CreateSerieDto | UpdateSerieDto) => {
    await seriesService.create(data as CreateSerieDto);
    toast.success('Série criada com sucesso!');
    loadSeries();
  };

  const handleUpdateSerie = async (data: CreateSerieDto | UpdateSerieDto) => {
    if (!editingSerie) return;
    await seriesService.update(editingSerie.id, data);
    toast.success('Série atualizada com sucesso!');
    loadSeries();
  };

  const handleToggleAtiva = async (serie: Serie) => {
    try {
      await seriesService.toggleAtiva(serie.id);
      toast.success(`Série ${serie.ativa ? 'desativada' : 'ativada'} com sucesso!`);
      loadSeries();
    } catch (error) {
      console.error('Erro ao alterar status da série:', error);
      toast.error('Não foi possível alterar o status da série');
    }
  };

  const handleDeleteSerie = async (serie: Serie) => {
    try {
      await seriesService.delete(serie.id);
      toast.success('Série excluída com sucesso!');
      loadSeries();
    } catch (error: any) {
      console.error('Erro ao excluir série:', error);
      if (error?.response?.status === 409) {
        toast.error('Não é possível excluir série com turmas vinculadas');
      } else {
        toast.error('Erro ao excluir série');
      }
    }
  };

  // Obter nomes existentes para validação
  const existingNames = series.map(serie => `${serie.nome}|${serie.tipoEnsino}`);

  if (loading) {
    return (
      <Layout title="Séries">
        <div className={styles.pageContainer}>
          <div className={styles.loadingState}>
            Carregando séries...
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Séries">
      <div className={styles.pageContainer}>
        <div className={styles.pageHeader}>
          <div className={styles.headerContent}>
            <h1 className={styles.pageTitle}>
              <BookOpen size={32} />
              Gestão de Séries
            </h1>
            <p className={styles.pageDescription}>
              Configure as séries curriculares e extracurriculares com seus respectivos valores de mensalidade.
            </p>
          </div>

          <div className={styles.actionsContainer}>
            <button
              className={`${styles.button} ${styles['button--primary']}`}
              onClick={() => setShowCreateModal(true)}
            >
              <Plus size={20} />
              Nova Série
            </button>
          </div>
        </div>

        {/* Estatísticas */}
        <div className={styles.statsContainer}>
          <div className={styles.statCard}>
            <div className={styles.statValue}>{stats.total}</div>
            <div className={styles.statLabel}>Total de Séries</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statValue}>{stats.ativas}</div>
            <div className={styles.statLabel}>Séries Ativas</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statValue}>{stats.curriculares}</div>
            <div className={styles.statLabel}>Curriculares</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statValue}>{stats.extracurriculares}</div>
            <div className={styles.statLabel}>Extracurriculares</div>
          </div>
        </div>

        {/* Filtros */}
        <div className={styles.filtersContainer}>
          <div className={styles.filtersRow}>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Tipo de Ensino</label>
              <select
                className={styles.select}
                value={tipoFilter}
                onChange={(e) => setTipoFilter(e.target.value as any)}
              >
                <option value="all">Todos os tipos</option>
                <option value="curricular">Curricular</option>
                <option value="extracurricular">Extracurricular</option>
              </select>
            </div>

            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Status</label>
              <select
                className={styles.select}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
              >
                <option value="all">Todos os status</option>
                <option value="ativa">Ativas</option>
                <option value="inativa">Inativas</option>
              </select>
            </div>

            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Buscar</label>
              <div className={styles.searchContainer}>
                <Search className={styles.searchIcon} />
                <input
                  type="text"
                  className={styles.searchInput}
                  placeholder="Buscar por nome..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Grid de Séries */}
        {filteredSeries.length > 0 ? (
          <div className={styles.seriesGrid}>
            {filteredSeries.map((serie) => (
              <SerieCard
                key={serie.id}
                serie={serie}
                onEdit={(selectedSerie) => setEditingSerie(selectedSerie)}
                onToggleAtiva={handleToggleAtiva}
                onDelete={(selectedSerie) => setDeletingSerie(selectedSerie)}
              />
            ))}
          </div>
        ) : (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>📚</div>
            <h3 className={styles.emptyTitle}>
              {searchTerm || tipoFilter !== 'all' || statusFilter !== 'all'
                ? 'Nenhuma série encontrada'
                : 'Nenhuma série cadastrada'
              }
            </h3>
            <p className={styles.emptyDescription}>
              {searchTerm || tipoFilter !== 'all' || statusFilter !== 'all'
                ? 'Tente ajustar os filtros para encontrar outras séries.'
                : 'Crie a primeira série para começar a organizar o ensino.'
              }
            </p>
            {!searchTerm && tipoFilter === 'all' && statusFilter === 'all' && (
              <button
                className={`${styles.button} ${styles['button--primary']}`}
                onClick={() => setShowCreateModal(true)}
              >
                <Plus size={20} />
                Criar Primeira Série
              </button>
            )}
          </div>
        )}

        {/* Modais */}
        <SerieFormModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateSerie}
          existingNames={existingNames}
        />

        <SerieFormModal
          isOpen={!!editingSerie}
          serie={editingSerie}
          onClose={() => setEditingSerie(null)}
          onSubmit={handleUpdateSerie}
          existingNames={existingNames}
        />

        <ConfirmDeleteSerieModal
          isOpen={!!deletingSerie}
          serie={deletingSerie}
          onClose={() => setDeletingSerie(null)}
          onConfirm={handleDeleteSerie}
        />
      </div>
    </Layout>
  );
};

export default SeriesPage;
